import type { ParameterDescriptor, ParameterId } from "./types";

const parameter = (
  numericId: number,
  id: ParameterId,
  label: string,
  shortLabel: string,
  owner: "video" | "audio",
  min: number,
  max: number,
  defaultValue: number,
  step: number,
  unit: string,
  curve: ParameterDescriptor["curve"] = "linear",
  fractionalBits = 15,
): ParameterDescriptor => ({
  id,
  numericId,
  label,
  shortLabel,
  owner,
  min,
  max,
  defaultValue,
  step,
  unit,
  curve,
  register: { width: 16, signed: min < 0, fractionalBits },
});

export const PARAMETER_CATALOG: readonly ParameterDescriptor[] = [
  parameter(0x0001, "video.horizontalJitter", "Horizontal sync disturbance", "H-SYNC", "video", 0, 1, 0.12, 0.01, "%"),
  parameter(0x0002, "video.verticalRoll", "Vertical roll", "V-ROLL", "video", -1, 1, 0, 0.01, "%", "bipolar"),
  parameter(0x0003, "video.chromaPhase", "Chroma phase", "CHROMA", "video", -180, 180, 0, 1, "°", "bipolar", 7),
  parameter(0x0004, "video.chromaGain", "Chroma gain", "COLOR", "video", 0, 2, 1, 0.01, "×", "linear", 14),
  parameter(0x0005, "video.noise", "Composite noise", "NOISE", "video", 0, 1, 0.08, 0.01, "%"),
  parameter(0x0006, "video.feedback", "Frame feedback", "FEEDBACK", "video", 0, 0.96, 0.18, 0.01, "%"),
  parameter(0x0007, "video.burstAmplitude", "Burst amplitude", "BURST", "video", 0, 1.5, 1, 0.01, "×", "linear", 14),
  parameter(0x0008, "video.linePosition", "Line selection", "LINE POS", "video", 0, 1, 0.48, 0.01, "%"),
  parameter(0x0009, "video.lineWidth", "Line width", "LINE WIDTH", "video", 0, 0.35, 0.05, 0.005, "%"),
  parameter(0x000a, "video.gain", "Video gain", "GAIN", "video", 0, 2, 1, 0.01, "×", "linear", 14),
  parameter(0x000b, "video.offset", "Video DC offset", "OFFSET", "video", -1, 1, 0, 0.01, "%", "bipolar"),
  parameter(0x000c, "video.syncOffset", "Sync timing offset", "SYNC OFFSET", "video", -8, 8, 0, 0.0625, "samples", "bipolar", 8),
  parameter(0x000d, "video.clipping", "Signal clipping", "CLIP", "video", 0, 1, 0, 0.01, "%"),
  parameter(0x000e, "video.solarize", "Solarization", "SOLARIZE", "video", 0, 1, 0, 0.01, "%"),
  parameter(0x0101, "synth.osc1Detune", "Oscillator one detune", "OSC 1 FINE", "audio", -100, 100, -7, 1, "ct", "bipolar", 7),
  parameter(0x0102, "synth.osc1Level", "Oscillator one level", "OSC 1", "audio", 0, 1, 0.72, 0.01, "%"),
  parameter(0x0103, "synth.osc2Detune", "Oscillator two detune", "OSC 2 FINE", "audio", -100, 100, 7, 1, "ct", "bipolar", 7),
  parameter(0x0104, "synth.osc2Level", "Oscillator two level", "OSC 2", "audio", 0, 1, 0.58, 0.01, "%"),
  parameter(0x0105, "synth.noiseLevel", "Synth noise level", "NOISE", "audio", 0, 1, 0.08, 0.01, "%"),
  parameter(0x0106, "synth.cutoff", "Filter cutoff", "CUTOFF", "audio", 40, 18000, 2400, 1, "Hz", "log", 0),
  parameter(0x0107, "synth.resonance", "Filter resonance", "RESONANCE", "audio", 0, 0.98, 0.22, 0.01, "%"),
  parameter(0x0108, "synth.attack", "Envelope attack", "ATTACK", "audio", 0.002, 4, 0.018, 0.001, "s", "log", 12),
  parameter(0x0109, "synth.decay", "Envelope decay", "DECAY", "audio", 0.005, 4, 0.2, 0.001, "s", "log", 12),
  parameter(0x010a, "synth.sustain", "Envelope sustain", "SUSTAIN", "audio", 0, 1, 0.62, 0.01, "%"),
  parameter(0x010b, "synth.release", "Envelope release", "RELEASE", "audio", 0.005, 8, 0.42, 0.001, "s", "log", 12),
  parameter(0x010c, "synth.lfoRate", "Low frequency oscillator rate", "LFO RATE", "audio", 0.02, 30, 1.7, 0.01, "Hz", "log", 10),
  parameter(0x010d, "synth.drive", "Output drive", "DRIVE", "audio", 0, 1, 0.18, 0.01, "%"),
] as const;

export const PARAMETER_BY_ID = new Map(PARAMETER_CATALOG.map((item) => [item.id, item]));

export const VIDEO_PARAMETERS = PARAMETER_CATALOG.filter((item) => item.owner === "video");
export const AUDIO_PARAMETERS = PARAMETER_CATALOG.filter((item) => item.owner === "audio");
